import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import helpService from '../services/HelpService';

const HelpContext = createContext({
    articles: [],
    loading: false,
    error: false,
    getForAnchor: () => [],
    reload: () => {},
});

/**
 * Loads the help articles visible to the current viewer once, and exposes them
 * to every HelpButton and the Help Center page. Refetches when the impersonated
 * client (loginAs) changes.
 */
export function HelpProvider({ loginAs = null, children }) {
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            const data = await helpService.getVisibleArticles(loginAs);
            setArticles(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error('Failed to load help articles', e);
            setArticles([]);
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [loginAs]);

    // Refetch when the impersonated client changes and on navigation, so
    // newly published content appears without requiring a hard refresh.
    const location = useLocation();
    useEffect(() => {
        load();
    }, [load, location.pathname]);

    const getForAnchor = useCallback(
        (anchor) => {
            const key = String(anchor ?? '').trim().toLowerCase();
            if (!key) return [];
            return articles.filter((a) =>
                (Array.isArray(a.anchorKeys) ? a.anchorKeys : [])
                    .some((k) => String(k ?? '').trim().toLowerCase() === key)
            );
        },
        [articles]
    );

    const value = useMemo(
        () => ({ articles, loading, error, getForAnchor, reload: load }),
        [articles, loading, error, getForAnchor, load]
    );

    return <HelpContext.Provider value={value}>{children}</HelpContext.Provider>;
}

export const useHelp = () => useContext(HelpContext);

export default HelpProvider;
